import { getEnvOrThrow, useMonkAppState } from '@monkvision/common';
import { useMonkApi } from '@monkvision/network';
import { SteeringWheelPosition } from '@monkvision/types';
import { useCallback } from 'react';
import { DashboardInspectionStatus } from '../useInspectionList/types';
import { CreateInspectionParams, DashboardAdditionalData } from './types';

const CAPTURE_APP_URL = getEnvOrThrow('REACT_APP_CAPTURE_APP_URL');

export interface GetInspectionParams {
  inspectionId: string;
}

export function useCreateInspectionLink() {
  const { config, authToken } = useMonkAppState();
  const { createInspection } = useMonkApi({
    authToken: authToken ?? '',
    apiDomain: config.apiDomain,
    thumbnailDomain: config.thumbnailDomain,
  });

  const createInspectionLink = useCallback(
    async (params: CreateInspectionParams) => {
      if (!config.allowCreateInspection) {
        throw new Error(
          'Unable to create a new inspection because config.allowCreateInspection is set to false',
        );
      }
      if (!authToken) {
        throw new Error('Unable to create a new inspection because auth token is not defined');
      }
      const additionalData: DashboardAdditionalData = {
        customer_status: DashboardInspectionStatus.INSPECTION_LINK_SEND,
        last_update_date: new Date().toISOString(),
        steering_wheel_position: SteeringWheelPosition.LEFT,
      };
      const { id } = await createInspection({
        ...config.createInspectionOptions,
        vehicle: {
          vin: params.vin,
        },
        additionalData,
      });
      return `${CAPTURE_APP_URL}/?i=${id}&s=${SteeringWheelPosition.LEFT}&l=en-hag`;
    },
    [config, createInspection, authToken],
  );

  const getInspectionLink = useCallback(
    (params: GetInspectionParams): string => {
      if (!authToken) {
        throw new Error('Auth token is not defined');
      }
      return `${CAPTURE_APP_URL}/?i=${params.inspectionId}&s=${SteeringWheelPosition.LEFT}&l=en-hag`;
    },
    [authToken],
  );

  return { createInspectionLink, getInspectionLink };
}
